import { useState, useEffect, useMemo, useRef } from 'react'
import axios from 'axios'

import { usePrevious } from './useUserEmailTemplates'
import { useAdminURL } from '../contexts/Domain'

export interface Users {
  name: string;
  selected: boolean;
  eid: string;
}

interface UsersResponse {
    employeeDropdownListDtos: Array<Users>
    totalCount: number
    pageNumber: number
    pageSize: number
}

interface UseAccessGroupUsersProps {
    page: number
    keywords: string
    accessGroupEid: string
    onNewResults?: (response: UsersResponse) => void
    onNextPageResults?: (response: UsersResponse) => void
    onError?: (response: any) => void
}
export default function useAccessGroupUsers(props: UseAccessGroupUsersProps) {
    const adminURL = useAdminURL()

    const cancelRef = useRef(null)
    let { current: cancelUpdates } = cancelRef
    const [users, setUsers] = useState([])
    const [usersCount, setUsersCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const fetchNewResults = (hasPageChanged?: boolean) => {
        setLoading(true)
        setError('')
        axios.get<UsersResponse>(`${adminURL}/action/accessGroups/selectUsers/${props.accessGroupEid === '' ? null : props.accessGroupEid}?keywords=${props.keywords}&pageSize=10&pageNumber=${props.page}`)
        .then(response => {
                if (cancelUpdates) return

                setLoading(false)

                if (response.status === 200) {
                    setUsers(response.data.employeeDropdownListDtos)
                    setUsersCount(response.data.totalCount)
                    if (hasPageChanged) {
                        if (typeof props.onNextPageResults === 'function') {
                            props.onNextPageResults(response.data)
                        }
                    } else {
                        if (typeof props.onNewResults === 'function') {
                            props.onNewResults(response.data)
                        }
                    }
                } else {
                    setError('An error occurred while retrieving results')
                }
            })
            .catch(err => {
                if (cancelUpdates) return

                setLoading(false)
                setError('An error occurred while retrieving results')
                console.error(err)
            })
    }

    const prevPage = usePrevious(props.page)

    useEffect(() => {
        cancelUpdates = false
        const hasPageChanged = props.page !== prevPage
        fetchNewResults(hasPageChanged)

        return () => {
            cancelUpdates = true
        }
    }, [props.page, props.keywords])

    return useMemo(() => ({
        users,
        usersCount,
        loading,
        error,
    }), [
        users,
        usersCount,
        loading,
        error,
    ])
}
